import React, { useRef, useState, useEffect } from 'react';
import { FiCamera, FiScissors, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import './ImageUpload.css';

/**
 * ImageUpload Component
 * Handles image file selection and preview
 */
const ImageUpload = ({
  onImageSelect,
  selectedImage,
  onCropClick,
  uploadWarning,
  uploadProcessing,
}) => {
  const fileInputRef = useRef(null);
  const urlRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!selectedImage) {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedImage);
    urlRef.current = url;
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      urlRef.current = null;
      setPreviewUrl(null);
    };
  }, [selectedImage]);

  const handleFileSelect = async (file) => {
    if (file && file.type.startsWith('image/')) {
      await onImageSelect(file);
    } else {
      alert('Please select a valid image file (JPG, PNG, or WEBP)');
    }
  };

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileSelect(file);
    }
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      await handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (!uploadProcessing) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="image-upload-container">
      <h2 className="section-title">Upload Your Photo</h2>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="file-input"
        style={{ display: 'none' }}
        disabled={uploadProcessing}
      />

      {uploadWarning && (
        <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs flex items-start gap-2">
          <FiAlertCircle className="shrink-0 mt-0.5" />
          <span>{uploadWarning}</span>
        </div>
      )}

      {!selectedImage ? (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''} ${uploadProcessing ? 'opacity-60 pointer-events-none' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {uploadProcessing ? (
            <>
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="upload-text">Optimizing image...</p>
            </>
          ) : (
            <>
              <div className="upload-icon">
                <FiCamera />
              </div>
              <p className="upload-text">
                Click to upload or drag and drop
              </p>
              <p className="upload-hint">
                JPG, PNG, WEBP — large files are auto-optimized in your browser
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="image-preview-container">
          <div className="preview-image-wrapper">
            <img
              src={previewUrl}
              alt="Selected"
              className="preview-image"
            />
            <div className="image-overlay-actions">
              <button
                className="action-btn crop-btn"
                onClick={onCropClick}
                title="Crop Image"
                disabled={uploadProcessing}
              >
                <FiScissors /> <span>Crop</span>
              </button>
              <button
                className="action-btn change-btn"
                onClick={handleClick}
                title="Change Image"
                disabled={uploadProcessing}
              >
                <FiRefreshCw /> <span>Change</span>
              </button>
            </div>
          </div>
          <div className="image-info">
            <p className="image-name">{selectedImage.name}</p>
            <p className="image-size">{(selectedImage.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
